import React from 'react';

import { FileRejection, useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch } from '../../common/hooks/reduxHooks';
import { addProcessedOcrs } from '../../common/stores/modelSlice';
import { UploadableFile } from '../../common/types/UploadableFile';
import { ProcessedOcr } from '../types/ProcessedOcr';

export const useFileDrop = (disabled: boolean) => {
  const dispatch = useAppDispatch();
  const [error, setError] = React.useState('');

  const handleError = (files: FileRejection[]) => {
    const messages = files
      .flatMap((file) => file.errors.flatMap((error) => error.message + ' \n'))
      .join();
    setError(messages);
  };

  const onDrop = React.useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      const mappedFiles: UploadableFile[] = acceptedFiles.map((file) => ({
        file,
        errors: [],
        id: uuidv4(),
        type: file.type,
        size: file.size,
        name: file.name,
      }));
      const mappedOcrs: ProcessedOcr[] = mappedFiles.map((file) => ({
        error: '',
        uploadUrl: '',
        isUploading: false,
        uploadComplete: false,
        wrappedFile: file,
        id: file.id ?? '',
        savedSheetId: -1,
        fileName: file.name,
        ocrResult: undefined,
        thumbnail: undefined,
        clinicNote: false,
      }));
      handleError(rejectedFiles);
      dispatch(addProcessedOcrs(mappedOcrs));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxSize: 15000000,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/tiff': ['.tif', '.tiff'],
      'text/plain': ['.txt'],
      'application/vnd.oasis.opendocument.text': ['.odt'],
    },
    disabled,
  });

  return {
    error,
    getRootProps,
    getInputProps,
  };
};
